import React, { useEffect, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

import CheckIcon from '@material-ui/icons/Check';
import InfoIcon from '@material-ui/icons/Info';

import { RailzButton } from '@railzai/railz-uikit-react';

import { useDispatch, useSelector } from 'react-redux';

import { Connection } from '../../types/interfaces';
import { ActionTypes, ConnectionStatus } from '../../types/enums';

import BusinessDisconnect from './business-disconnect/business-disconnect';
import useStyle from './style';

import { getConnectionServiceName } from 'helpers/common.helper';
import { useBusinessContext } from 'pages/business/provider/business-event';
import AlertDialog from 'components/dialog';
import SyncIcon from 'assets/icons/sync';
import storeService from 'store';
import { openNewTab } from 'helpers/open-new-tab';
import { getSyncState } from 'store/features/sync/sync.selector';
import { SyncState } from 'store/features/sync/sync.state';
import { fetchSyncStatuses, syncData } from 'store/features/sync/sync.action';
import { AspType } from 'types';

const BusinessManageServices: React.FC<{
  updateBusiness: () => void;
}> = ({ updateBusiness }) => {
  const classes = useStyle();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const store = storeService.getStore();

  const [selectedConnection, setSelectedConnection] = useState<Connection>();
  const syncState = useSelector<ReturnType<typeof store.getState>, SyncState>(getSyncState);

  const NO_SYNC_ASPS = [AspType.QUICKBOOKS_DESKTOP, AspType.BYOD];

  const {
    event,
    setEvent,
    contextBusiness,
    openDisconnectBusinessModal,
    setOpenDisconnectBusinessModal,
    shouldFetchSync,
    setShouldFetchSync,
  } = useBusinessContext();

  const headerTable = [
    {
      name: t('DASHBOARD_MANAGE_SERVICES_SERVICE'),
    },
    {
      name: t('DASHBOARD_MANAGE_SERVICES_STATUS'),
    },
    {
      name: t('DASHBOARD_MANAGE_SERVICES_SYNC'),
    },
    {
      name: t('DASHBOARD_MANAGE_SERVICES_ACTION'),
    },
  ];

  useEffect(() => {
    if (contextBusiness?.connections?.length && shouldFetchSync) {
      dispatch(
        fetchSyncStatuses({
          syncType: 'full',
          connections: contextBusiness.connections.map((connection) => connection.connectionId),
        }),
      );
      setShouldFetchSync(false);
    }
  }, [contextBusiness, shouldFetchSync]);

  const connectionsMapped = contextBusiness?.connections
    ?.filter((connection) => connection.status !== ConnectionStatus.PENDING)
    .sort((serviceA, serviceB) => {
      const textA = serviceA.status.toUpperCase();
      const textB = serviceB.status.toUpperCase();
      return textA < textB ? -1 : textA > textB ? 1 : 0;
    })
    .map((connection) => ({
      id: connection.connectionId,
      name: getConnectionServiceName(connection),
      status: (
        <Chip
          className={
            connection.status === ConnectionStatus.ACTIVE
              ? classes.pillConnected
              : classes.pillDisconnected
          }
          icon={connection.status === ConnectionStatus.ACTIVE && <CheckIcon />}
          label={
            connection.status === ConnectionStatus.ACTIVE
              ? t('DASHBOARD_MANAGE_SERVICES_CONNECTED')
              : t('DASHBOARD_MANAGE_SERVICES_DISCONNECTED')
          }
          size="small"
        />
      ),
      sync:
        connection.status == ConnectionStatus.ACTIVE ? (
          <div
            key={connection.connectionId}
            className={
              ['completed', 'error'].includes(
                syncState.syncs?.find((s) => s.connectionUuid == connection.connectionId)?.status,
              ) &&
              !NO_SYNC_ASPS.includes(connection.serviceName) &&
              !syncState.loading
                ? classes.syncSection
                : classes.syncSectionDisabled
            }
            onClick={(): void => {
              ['completed', 'error'].includes(
                syncState.syncs?.find((s) => s.connectionUuid == connection.connectionId)?.status,
              ) &&
                !NO_SYNC_ASPS.includes(connection.serviceName) &&
                !syncState.loading &&
                dispatch(syncData({ connectionUuid: connection.connectionId, fullSync: true }));
            }}
          >
            <span className={classes.syncIcon}>
              <SyncIcon
                color={!NO_SYNC_ASPS.includes(connection.serviceName) ? '#1D1F48' : '#757575'}
              />{' '}
            </span>{' '}
            <span
              className={
                NO_SYNC_ASPS.includes(connection.serviceName) || syncState.loading
                  ? classes.syncDisabled
                  : classes.syncText
              }
            >
              {' '}
              {['completed', 'error'].includes(
                syncState.syncs?.find((s) => s.connectionUuid == connection.connectionId)?.status,
              ) ||
              !!syncState.error ||
              syncState.loading
                ? t('DASHBOARD_MANAGE_SERVICES_SYNC_DATA')
                : t('DASHBOARD_MANAGE_SERVICES_SYNC_LOADING')}
            </span>
          </div>
        ) : (
          <></>
        ),
      action: connection.status === ConnectionStatus.ACTIVE && (
        <RailzButton
          data-testid={`disconnect-button-${connection.connectionId}`}
          type="text warning"
          onButtonClick={(): void => {
            setSelectedConnection(connection);
            setOpenDisconnectBusinessModal(true);
          }}
          label={t('DASHBOARD_MANAGE_SERVICES_DISCONNECT')}
        />
      ),
    }));

  const handleUpdateBusiness = (): void => {
    updateBusiness();
    setEvent(null);
  };

  return (
    <>
      <AlertDialog
        testId="business-manage-services"
        showCloseButton
        isOpen={
          !!contextBusiness && !openDisconnectBusinessModal && event === ActionTypes.MANAGE_SERVICES
        }
        title={t('DASHBOARD_MANAGE_SERVICES_HEADER')}
        onClose={(): void => {
          setEvent(null);
        }}
        maxWidth="sm"
        classDialog={classes.dialog}
      >
        <>
          <div className={classes.manageServicesDescription}>
            <Trans
              i18nKey="DASHBOARD_BUSINESS_MANAGE_SERVICES_SUBTEXT"
              components={{
                a: (
                  <a
                    onClick={(e): void => openNewTab(e, '/developers/webhook-logs')}
                    rel="noopener noreferrer"
                    href="/developers/webhook-logs"
                    className={classes.webhookLink}
                  >
                    {t('DASHBOARD_WEBHOOKS_PAGE_CTA')}
                  </a>
                ),
              }}
            />
          </div>
          <Table data-testid="business-manage-services-table">
            <TableHead>
              <TableRow>
                {headerTable.map(({ name }) => (
                  <TableCell key={name}>{name}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {connectionsMapped?.map(({ id, name, status, sync, action }) => (
                <TableRow key={id}>
                  <TableCell>
                    <Typography className={classes.nameRowLabel}>{name}</Typography>
                    <Typography className={classes.uuidRowLabel}>{id}</Typography>
                  </TableCell>
                  <TableCell>{status}</TableCell>
                  <TableCell>{sync}</TableCell>
                  <TableCell>{action}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className={classes.manageServicesBottonText}>
            <Trans
              i18nKey={
                contextBusiness?.connections?.find((c) => c.isMockData)
                  ? 'DASHBOARD_BUSINESS_MANAGE_SERVICES_BOTTOM_TEXT_SANDBOX'
                  : 'DASHBOARD_BUSINESS_MANAGE_SERVICES_BOTTOM_TEXT'
              }
              components={{
                icon: <InfoIcon className={classes.infoIcon} />,
              }}
            />
          </div>
        </>
      </AlertDialog>
      <BusinessDisconnect
        connection={{ ...selectedConnection, businessName: contextBusiness?.businessName }}
        updateBusiness={handleUpdateBusiness}
      />
    </>
  );
};

export default BusinessManageServices;
